/**
 * SAMLRedirectGuard
 *
 * Gate for /org/:orgId/* routes. The actual SSO redirect and membership
 * provisioning are handled by `LandingPage`, so the guard's only job is:
 *   - Not signed in?         → send the user back to `/` (landing page),
 *                              which will kick off the SAML SSO flow.
 *   - Signed in but not a
 *     member of :orgId?      → send them back to `/` so the landing page
 *                              effect can route them to their sp_org_id
 *                              and run the `ensure-org-membership` safety
 *                              net.
 *   - Signed in & a member?  → render the protected children.
 */

import { useAuth, useOrganizationList } from '@clerk/clerk-react'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingScreen from '../../components/Loader/Loader'

export default function SAMLRedirectGuard({ children }) {
  const { orgId: routeOrgId } = useParams()
  const { isSignedIn, isLoaded: authLoaded } = useAuth()
  const { userMemberships, isLoaded: orgsLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  })
  const navigate = useNavigate()

  useEffect(() => {
    if (!authLoaded) return

    if (!isSignedIn) {
      navigate('/', { replace: true })
      return
    }

    if (!routeOrgId || !orgsLoaded) return

    const isMember = userMemberships?.data?.some((m) => m.organization.id === routeOrgId)
    if (!isMember) {
      navigate('/', { replace: true })
    }
  }, [authLoaded, isSignedIn, orgsLoaded, routeOrgId, userMemberships?.data, navigate])

  if (!authLoaded || (isSignedIn && !orgsLoaded)) {
    return <LoadingScreen message="Verifying access…" />
  }

  if (!isSignedIn) return <LoadingScreen message="Redirecting…" />

  const isMember = userMemberships?.data?.some((m) => m.organization.id === routeOrgId)
  if (routeOrgId && !isMember) return <LoadingScreen message="Redirecting…" />

  return children
}
